<script lang="ts" setup>
import './modal-ageverify.scss';
import { ref, onMounted, reactive, watch, onBeforeUnmount } from 'vue';
import { post } from '@/api/fetch-extension';
import * as faceapi from 'face-api.js';

import { FaceDetector } from './facedetector.js';
import { verifyAge } from '@/api/biodata/index.js';
import { useUserStore } from '@/stores/user.js';
import { useModalsStore } from '@/stores/modals';

import { sleep } from '../../../utils';

const { setAgeVerified } = useUserStore();
const { closeModal } = useModalsStore();
const { account } = useUserStore();

const video: any = ref(null);
const statusMessage = ref({class: 'info', msg: 'init'});
const imageBase64 = ref('');
const scale: any = ref(null);
const inPlace: boolean | any = ref(false);
const isPresent = ref(false);
const track: any = ref(null);

const placeholder: any = ref(null);
const detector: any = ref(null);
const detectorShow: boolean | any = ref(false);
const detectorCanvas: any = ref(null);
const devices = ref([]);
const approve = ref(false);
const base = import.meta.env.BASE_URL;

const constraints = reactive({
    audio: false,
    video: {
        facingMode: 'user',
        width: { min: 640, ideal: 640 },
        height: { min: 480, ideal: 480 }
    }
});

async function load() {
    approve.value = true;
    await sleep(3000);
    setupCamera();
    loadFaceAPI();
}

watch(devices, newDevices => {
    console.log('Devices updated:', newDevices);
});

const setupCamera = () => {
    if (video.value) {
        video.value.addEventListener('play', adjustVideo);
        navigator.mediaDevices
            .getUserMedia(constraints)
            .then(stream => {
                track.value = stream.getVideoTracks()[0];
                video.value.srcObject = stream;
                navigator.mediaDevices.enumerateDevices().then(gotDevices);
            })
            .catch(err => {
                console.error('getUserMedia error!', err);
            });
    }
};

const loadFaceAPI = async () => {
    detectorShow.value = true;
    const modelsurl = new URL(`${base}models/`, import.meta.url).href;
    await faceapi.loadTinyFaceDetectorModel(modelsurl);
    detector.value = new FaceDetector(video.value, 150);
    detector.value.addEventListener('ready', onDetectionReady);
    detector.value.addEventListener('face', onFaceDetection);
    detector.value.addEventListener('bounds', onDetectionBounds);
    detector.value.start();
};

const onDetectionReady = () => {
    console.log('detector ready');
};

const onFaceDetection = (event: any) => {
    isPresent.value = event.isPresent;
    if (!isPresent.value) {
        statusMessage.value = {class: 'info', msg: 'front-camera'};
    }
};

const onDetectionBounds = (event: any) => {
    drawFrame(event);
    inPlace.value = false;
    const tolerance = 20 / scale.value;
    const w = video.value.videoWidth;
    const h = video.value.videoHeight;

    if (w / 2 + tolerance < event.x * 1.25 + (event.width * 0.75) / 2) {
        statusMessage.value = {class: 'info', msg: 'move-right'};
    } else if (w / 2 - tolerance > event.x * 1.25 + (event.width * 0.75) / 2) {
        statusMessage.value = {class: 'info', msg: 'move-left'};
    } else if (h / 2 + tolerance < event.y + (event.height * 0.75) / 2) {
        statusMessage.value = {class: 'info', msg: 'move-up'};
    } else if (h / 2 - tolerance > event.y + (event.height * 0.75) / 2) {
        statusMessage.value = {class: 'info', msg: 'move-down'};
    } else {
        let closeness = false;
        if (w >= h) closeness = event.height > h * 0.45;
        else closeness = event.width > w * 0.45;

        if (!closeness) {
            statusMessage.value = {class: 'info', msg: 'closer-camera'};
        } else {
            inPlace.value = true;
            statusMessage.value = {class: 'info', msg: 'age-detecting'};
            detector.value.stop();
            capture();
        }
    }
};

const capture = async () => {
    const canvas = document.createElement('canvas');
    const ctx: any = canvas.getContext('2d');
    const w = video.value.videoWidth;
    const h = video.value.videoHeight;

    if (w > h) {
        canvas.width = h;
        canvas.height = h;
        ctx.drawImage(video.value, -(w - h) / 2, 0, w, h);
    } else {
        canvas.width = w;
        canvas.height = w;
        ctx.drawImage(video.value, 0, -(h - w) / 2, w, h);
    }

    // Get image
    imageBase64.value = canvas.toDataURL('image/jpeg').substr(23);

    // Remove detection frame
    detectorShow.value = false;

    // Send image to api
    detectFaceAge();

    // Stop the video stream
    if (track.value) track.value.stop();
    video.value.srcObject = null;
};

const detectFaceAge = async () => {
    const { result, error } = await verifyAge(account.id, imageBase64.value);
    if (result) {
        statusMessage.value = result.status === 'APPROVED' ? {class: 'success', msg: 'status.APPROVED'} : {class: 'error', msg: 'status.REJECTED'};
        await sleep(3000);
        if(result.status === 'APPROVED'){ 
            closeModal('ageverify');
            setAgeVerified();
        } else {
            load();
        }
    } else {
        statusMessage.value = {class: 'error', msg: 'status.REJECTED'};
        await sleep(3000);
        load();
    }
};

const drawFrame = (bounds: any) => {
    const ctx = detectorCanvas.value.getContext('2d');
    ctx.clearRect(0, 0, detectorCanvas.value.width, detectorCanvas.value.height);
    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255, 40, 0, 1)';
    ctx.rect(bounds.x * 1.25 * scale.value, bounds.y * 0.5 * scale.value, bounds.width * 0.75 * scale.value, bounds.height * 0.75 * scale.value);
    ctx.stroke();
    ctx.closePath();
};

const gotDevices = (deviceInfos: any) => {
    devices.value = deviceInfos
        .filter((deviceInfo: any) => deviceInfo.kind === 'videoinput')
        .map((deviceInfo: any, index: any) => {
            return {
                value: deviceInfo.deviceId,
                text: deviceInfo.label || `camera ${index}`
            };
        });
};

const adjustVideo = () => {
    const frame: any = placeholder.value;
    const size = frame.offsetWidth;
    const w = video.value.videoWidth;
    const h = video.value.videoHeight;

    if (w >= h) {
        const ratio = w / h;
        scale.value = (size * ratio) / w;
        video.value.style.width = `${size * ratio}px`;
        video.value.style.height = `${size}px`;
        video.value.style.marginLeft = `${(size * ratio - size) / -2}px`;
    } else {
        const ratio = h / w;
        scale.value = (size * ratio) / h;
        video.value.style.height = `${size * ratio}px`;
        video.value.style.width = `${size}px`;
        video.value.style.marginTop = `${(size * ratio - size) / -2}px`;
    }
};

onBeforeUnmount(() => {
    if(approve.value){
        track.value.stop();
    }
});
</script>

<template src="./modal-ageverify.html"></template>
